import React from 'react'

import { usePlatformFeatureFlags } from '@components/utils/DashboardFeatureFlags/usePlatformFeatureFlags'
import { LeavingGuard } from '@components/utils/LeavingGuard'
import {
  ButtonClean,
  HeadContent,
  Loading,
  SaveShortcutListener,
  Select,
  type SelectOption,
  Subsection,
} from '@fourthwall/components'
import { useFormikContext } from '@fourthwall/utils/dist/formik'
import { useAdminModeContext } from '@modules/App/providers/AdminModeProvider'
import { ConfirmBar } from '@modules/Dashboard/modules/ConfirmBar'
import { routing } from '@utils/routing'

import * as S from './Shipping.styled'
import type { FormValues } from './Shipping.types'
import { FixedShipping } from './components/FixedShipping/FixedShipping'
import { ShippingLocation } from './components/ShippingLocation/ShippingLocation'
import { WeightedShipping } from './components/WeightedShipping/WeightedShipping'
import { InventoryShipmentSection } from './modules/InventoryShipmentSection/InventoryShipmentSection'
import { ShippingGroupsYouShipContainer } from './modules/ShippingGroupsYouShip/ShippingGroupsYouShipContainer'

export type ShippingViewProps = {
  shippingPricingMethodOptions: SelectOption[]
  isLoading: boolean
  isSubmitting: boolean
}

export const ShippingView = ({
  shippingPricingMethodOptions,
  isLoading,
  isSubmitting,
}: ShippingViewProps) => {
  const formik = useFormikContext<FormValues>()
  const { hasFeatureFlag } = usePlatformFeatureFlags()
  const adminMode = useAdminModeContext()

  const resetFixedShippingForm = () => {
    formik.setFieldTouched('domesticFirst', false)
    formik.setFieldTouched('domesticAdditional', false)
    formik.setFieldTouched('internationalFirst', false)
    formik.setFieldTouched('internationalAdditional', false)
  }

  const resetWeightedShippingForm = () => {
    formik.setFieldTouched('additionalShippingFee', false)
  }

  const handleValueChange = (value: string) => {
    if (value === 'Fixed') {
      resetFixedShippingForm()
    }

    if (value === 'Weighted') {
      resetWeightedShippingForm()
    }
  }

  return (
    <Loading isLoading={isLoading}>
      <HeadContent title="Shipping &ndash; Settings" />

      <Subsection title="Shipping pricing for products you ship">
        <Select
          label="Shipping pricing method"
          options={shippingPricingMethodOptions}
          {...formik.getFieldProps('pricingMethod')}
          onValueChange={handleValueChange}
        />
      </Subsection>

      <S.ShippingPricingMethodContainer>
        {formik.values.pricingMethod === 'Fixed' && <FixedShipping />}
        {formik.values.pricingMethod === 'Weighted' && <WeightedShipping />}
      </S.ShippingPricingMethodContainer>

      <S.ShippingLocationContainer>
        <ShippingLocation />
      </S.ShippingLocationContainer>

      <S.ShippingGroupsYouShipContainer>
        <ShippingGroupsYouShipContainer />
      </S.ShippingGroupsYouShipContainer>

      <Subsection title="Shipping pricing for products we ship for you">
        Your supporters will pay the true cost of shipping based on the weight of their order.
      </Subsection>

      {(hasFeatureFlag('assisted_product_shipping') || Boolean(adminMode?.isActive)) && (
        <Subsection
          title="Inventory shipments"
          rightContent={
            <ButtonClean
              label="Send inventory to our fulfillment center"
              to={routing.settings.shipping.inventoryShipments.create}
            />
          }
        >
          <InventoryShipmentSection />
        </Subsection>
      )}

      <SaveShortcutListener disabled={!formik.dirty} onSave={formik.submitForm} />
      <ConfirmBar
        isOpen={formik.dirty}
        onConfirm={formik.submitForm}
        onCancel={formik.resetForm}
        isLoading={isSubmitting}
      />
      <LeavingGuard when={formik.dirty} isValid={formik.isValid} onSave={formik.submitForm} />
    </Loading>
  )
}
